const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY2;
const PLAYLIST_ID = 'PLDZjxSO4MSJoA638nQvykQaevGgc-sV2B';
const COMMENT_SECTION_CHANNEL_ID = 'UC7bYyWCCCLHDU0ZuNzGNTtg';
const CACHE_KEY = `portfolio-stats:${PLAYLIST_ID}:v2`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export interface PortfolioStats {
  totalViews: number;
  totalVideos: number;
  totalChannels: number;
  totalLikes: number;
}

interface CachedPortfolioStats {
  cachedAt: number;
  stats: PortfolioStats;
}

const COMMENT_SECTION_STATS = {
  totalViews: 119_167_506,
  totalVideos: 199,
  totalLikes: 5_573_002,
};

const ANALYZER_PORTFOLIO_STATS: PortfolioStats = {
  totalViews: 155_703_935,
  totalVideos: 446,
  totalChannels: 19,
  totalLikes: 6_659_682,
};

const isPortfolioStats = (value: unknown): value is PortfolioStats => {
  if (!value || typeof value !== 'object') return false;

  const stats = value as PortfolioStats;
  return (
    typeof stats.totalViews === 'number' &&
    typeof stats.totalVideos === 'number' &&
    typeof stats.totalChannels === 'number' &&
    typeof stats.totalLikes === 'number'
  );
};

const getCachedStats = (allowExpired = false): PortfolioStats | null => {
  try {
    if (typeof window === 'undefined') return null;

    const cached = window.localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached) as CachedPortfolioStats;
    const isExpired = Date.now() - parsed.cachedAt > CACHE_TTL_MS;

    if (!allowExpired && isExpired) return null;
    if (!isPortfolioStats(parsed.stats)) return null;

    return parsed.stats;
  } catch {
    return null;
  }
};

const cacheStats = (stats: PortfolioStats): void => {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        cachedAt: Date.now(),
        stats,
      })
    );
  } catch {
    // Browsers can block storage or evict quota; failing open keeps the page usable.
  }
};

export const fetchPortfolioStats = async (): Promise<PortfolioStats> => {
  const cachedStats = getCachedStats();
  if (cachedStats) return cachedStats;

  const videos = new Map<string, { views: number; likes: number; channelId: string }>();
  let nextPageToken = '';

  try {
    if (!API_KEY) {
      throw new Error('Missing YouTube API key.');
    }

    do {
      const playlistRes = await fetch(
        `https://www.googleapis.com/youtube/v3/playlistItems?` +
        `part=contentDetails&playlistId=${PLAYLIST_ID}&key=${API_KEY}&maxResults=50` +
        (nextPageToken ? `&pageToken=${nextPageToken}` : '')
      );
      const playlistData = await playlistRes.json();
      if (!playlistRes.ok || playlistData.error) {
        throw new Error('Unable to fetch YouTube playlist data.');
      }
      if (!playlistData.items?.length) break;

      const videoIds = playlistData.items
        .map((i: any) => i.contentDetails.videoId)
        .join(',');

      const videoRes = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?` +
        `part=statistics,snippet&id=${videoIds}&key=${API_KEY}`
      );
      const videoData = await videoRes.json();
      if (!videoRes.ok || videoData.error) {
        throw new Error('Unable to fetch YouTube video statistics.');
      }

      for (const v of videoData.items || []) {
        if (v.snippet.channelId === COMMENT_SECTION_CHANNEL_ID) continue;

        videos.set(v.id, {
          views: parseInt(v.statistics.viewCount) || 0,
          likes: parseInt(v.statistics.likeCount) || 0,
          channelId: v.snippet.channelId,
        });
      }

      nextPageToken = playlistData.nextPageToken || '';
    } while (nextPageToken);

    const availableVideos = [...videos.values()];
    const channelIds = new Set(availableVideos.map(v => v.channelId));
    channelIds.add(COMMENT_SECTION_CHANNEL_ID);

    const liveStats = {
      totalViews: availableVideos.reduce((s, v) => s + v.views, COMMENT_SECTION_STATS.totalViews),
      totalLikes: availableVideos.reduce((s, v) => s + v.likes, COMMENT_SECTION_STATS.totalLikes),
      totalVideos: availableVideos.length + COMMENT_SECTION_STATS.totalVideos,
      totalChannels: channelIds.size,
    };
    const stats = {
      totalViews: Math.max(liveStats.totalViews, ANALYZER_PORTFOLIO_STATS.totalViews),
      totalLikes: Math.max(liveStats.totalLikes, ANALYZER_PORTFOLIO_STATS.totalLikes),
      totalVideos: Math.max(liveStats.totalVideos, ANALYZER_PORTFOLIO_STATS.totalVideos),
      totalChannels: Math.max(liveStats.totalChannels, ANALYZER_PORTFOLIO_STATS.totalChannels),
    };

    cacheStats(stats);
    return stats;
  } catch {
    return ANALYZER_PORTFOLIO_STATS;
  }
};
